import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Table,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  message,
  Empty,
  Statistic,
  Row,
  Col,
  Card,
  Tag,
  Input,
  Form,
  Modal,
  Alert,
  Divider
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  HomeOutlined,
  LockOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import * as XLSX from 'xlsx';
import { AFTER_SALES_STATUS_TEXT, AfterSalesRequest } from '../types/afterSales';
import '../pages/MemberManagement.css';

const { Text, Paragraph, Title } = Typography;
const { RangePicker } = DatePicker;

const BILLING_TOKEN_KEY = 'merchantBillingToken';

type BillingTeam = {
  teamId: string;
  teamName: string;
  teamCode?: string;
  authorized?: boolean;
};

type LineItem = {
  name?: string;
  model?: string;
  specification?: string;
  cas?: string;
  vendor?: string;
  brand?: string;
  unit?: string;
  amount?: string;
  price?: string;
  total?: string;
  purchaseChannel?: string;
  brandDiscountRate?: number;
  discountedTotal?: number;
};

type BillingOrder = {
  _id: string;
  category: string;
  applicant: string;
  itemSummary?: string;
  settleAmount: number;
  discountedAmount?: number | null;
  afterSales?: AfterSalesRequest[];
  afterSalesAdjustment?: number;
  billingAdjustedAmount?: number;
  completedAt?: string | null;
  shippedAt?: string | null;
  acceptedAt?: string | null;
  shippingAddress?: string;
  recipientName?: string;
  recipientPhone?: string;
  trackingNumbers?: Array<{ number: string; linkedAt?: string }>;
  consumableProjects?: LineItem[];
  reagentProjects?: LineItem[];
  merchantOrder?: {
    originalAmount?: number;
    quotedAmount?: number | null;
    platformOrderNote?: {
      completed?: boolean;
      price?: number | null;
      link?: string;
      submittedAt?: string | null;
    };
  };
};

type BillingData = {
  teamId: string;
  teamName: string;
  summary: {
    orderCount: number;
    totalAmount: number;
    discountedTotalAmount?: number;
    afterSalesAdjustmentAmount?: number;
    billingAdjustedTotalAmount?: number;
  };
  orders: BillingOrder[];
};

type StatusData = {
  passwordConfigured: boolean;
  emailMasked: string;
  hasEmail: boolean;
  locked?: boolean;
};

type ExportLog = {
  _id: string;
  createdAt: string;
  exporterName?: string;
  exporterUsername?: string;
  teamName?: string;
  startDate?: string;
  endDate?: string;
  orderCount: number;
  totalAmount: number;
  ip?: string;
  location?: string;
  device?: string;
};

async function merchantFetch(path: string, options?: RequestInit & { billing?: boolean }) {
  const token = localStorage.getItem('token');
  const billingToken = sessionStorage.getItem(BILLING_TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.billing && billingToken ? { 'X-Merchant-Billing-Token': billingToken } : {}),
    ...((options?.headers as Record<string, string>) || {})
  };
  const res = await fetch(`/api/merchant${path}`, { ...options, headers });
  const body = await res.json().catch(() => ({ success: false, error: '响应解析失败' }));
  if (body?.code === 'BILLING_LOCKED') {
    sessionStorage.removeItem(BILLING_TOKEN_KEY);
  }
  return body;
}

function fmtMoney(n?: number | null) {
  return `¥${Number(n || 0).toFixed(2)}`;
}

function fmtDate(v?: string | null) {
  if (!v) return '—';
  return dayjs(v).format('YYYY-MM-DD HH:mm');
}

function lineItemsOf(order: BillingOrder): LineItem[] {
  if (order.category === '试剂申请') return order.reagentProjects || [];
  return order.consumableProjects || [];
}

function writeExcel(billing: BillingData, range: [Dayjs, Dayjs]) {
  const teamName = billing.teamName || '团队';
  const headerRows: Array<Record<string, string | number>> = [];
  for (const order of billing.orders) {
    const items = lineItemsOf(order);
    const tracking = (order.trackingNumbers || []).map((t) => t.number).join('、') || '—';
    if (!items.length) {
      headerRows.push({
        订单编号: String(order._id),
        完成时间: fmtDate(order.completedAt),
        类型: order.category,
        申请人: order.applicant,
        摘要: order.itemSummary || '',
        结算金额: Number(order.settleAmount || 0),
        优惠后价格: order.discountedAmount ?? '',
        售后状态: (order.afterSales || []).map((row) => AFTER_SALES_STATUS_TEXT[row.status]).join('、'),
        售后调整金额: order.afterSalesAdjustment ?? 0,
        售后调整后金额: order.billingAdjustedAmount ?? order.discountedAmount ?? order.settleAmount,
        原报价: Number(order.merchantOrder?.originalAmount || 0),
        确认价: order.merchantOrder?.quotedAmount ?? '',
        备注价格: order.merchantOrder?.platformOrderNote?.submittedAt
          ? order.merchantOrder.platformOrderNote.price ?? ''
          : '',
        备注链接: order.merchantOrder?.platformOrderNote?.submittedAt
          ? order.merchantOrder.platformOrderNote.link || ''
          : '',
        收件人: order.recipientName || '',
        收件电话: order.recipientPhone || '',
        收货地址: order.shippingAddress || '',
        快递单号: tracking,
        明细序号: '',
        品名: '',
        规格型号: '',
        数量: '',
        单价: '',
        小计: '',
        明细优惠后价格: '',
        单位: '',
        采购渠道: ''
      });
      continue;
    }
    items.forEach((item, idx) => {
      headerRows.push({
        订单编号: String(order._id),
        完成时间: fmtDate(order.completedAt),
        类型: order.category,
        申请人: order.applicant,
        摘要: order.itemSummary || '',
        结算金额: idx === 0 ? Number(order.settleAmount || 0) : '',
        优惠后价格: idx === 0 ? order.discountedAmount ?? '' : '',
        售后状态:
          idx === 0
            ? (order.afterSales || []).map((row) => AFTER_SALES_STATUS_TEXT[row.status]).join('、')
            : '',
        售后调整金额: idx === 0 ? order.afterSalesAdjustment ?? 0 : '',
        售后调整后金额:
          idx === 0 ? order.billingAdjustedAmount ?? order.discountedAmount ?? order.settleAmount : '',
        原报价: idx === 0 ? Number(order.merchantOrder?.originalAmount || 0) : '',
        确认价: idx === 0 ? order.merchantOrder?.quotedAmount ?? '' : '',
        备注价格:
          idx === 0 && order.merchantOrder?.platformOrderNote?.submittedAt
            ? order.merchantOrder.platformOrderNote.price ?? ''
            : '',
        备注链接:
          idx === 0 && order.merchantOrder?.platformOrderNote?.submittedAt
            ? order.merchantOrder.platformOrderNote.link || ''
            : '',
        收件人: idx === 0 ? order.recipientName || '' : '',
        收件电话: idx === 0 ? order.recipientPhone || '' : '',
        收货地址: idx === 0 ? order.shippingAddress || '' : '',
        快递单号: idx === 0 ? tracking : '',
        明细序号: idx + 1,
        品名: item.name || '',
        规格型号: item.model || item.specification || item.cas || '',
        数量: item.amount || '',
        单价: item.price || '',
        小计: item.total || '',
        明细优惠后价格: item.discountedTotal ?? '',
        单位: item.unit || '',
        采购渠道: item.purchaseChannel || item.vendor || ''
      });
    });
  }

  const summarySheet = XLSX.utils.aoa_to_sheet([
    ['团队', teamName],
    ['账单区间', `${range[0].format('YYYY-MM-DD')} ~ ${range[1].format('YYYY-MM-DD')}`],
    ['已完成订单数', billing.summary.orderCount],
    ['结算合计', billing.summary.totalAmount],
    ['优惠后合计', billing.summary.discountedTotalAmount ?? 0],
    ['售后调整合计', billing.summary.afterSalesAdjustmentAmount ?? 0],
    ['售后调整后合计', billing.summary.billingAdjustedTotalAmount ?? 0],
    ['导出时间', dayjs().format('YYYY-MM-DD HH:mm:ss')]
  ]);
  const detailSheet = XLSX.utils.json_to_sheet(headerRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, summarySheet, '汇总');
  XLSX.utils.book_append_sheet(wb, detailSheet, '订单明细');
  XLSX.writeFile(
    wb,
    `商家账单_${teamName}_${range[0].format('YYYYMMDD')}-${range[1].format('YYYYMMDD')}.xlsx`
  );
}

const MerchantBilling: React.FC = () => {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [unlocked, setUnlocked] = useState(() => !!sessionStorage.getItem(BILLING_TOKEN_KEY));
  const [bootLoading, setBootLoading] = useState(true);
  const [setupForm] = Form.useForm();
  const [unlockForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const [resetOpen, setResetOpen] = useState(false);
  const [codeSending, setCodeSending] = useState(false);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [teams, setTeams] = useState<BillingTeam[]>([]);
  const [activeTeamId, setActiveTeamId] = useState('');
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState<BillingData | null>(null);
  const [exportLogs, setExportLogs] = useState<ExportLog[]>([]);
  const [exporting, setExporting] = useState(false);

  const loadStatus = useCallback(async () => {
    setBootLoading(true);
    try {
      const body = await merchantFetch('/billing/status');
      if (!body.success) {
        message.error(body.error || '无法加载账单状态');
        return;
      }
      setStatus(body.data);
    } finally {
      setBootLoading(false);
    }
  }, []);

  const loadTeams = useCallback(async () => {
    const body = await merchantFetch('/billing/teams', { billing: true });
    if (body.code === 'BILLING_LOCKED') {
      setUnlocked(false);
      return;
    }
    if (!body.success) {
      message.error(body.error || '加载团队失败');
      setTeams([]);
      return;
    }
    const list: BillingTeam[] = body.data || [];
    setTeams(list);
    setActiveTeamId((prev) => prev || (list[0]?.teamId ?? ''));
  }, []);

  const loadBilling = useCallback(async () => {
    if (!unlocked || !activeTeamId) {
      setBilling(null);
      return;
    }
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        teamId: activeTeamId,
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD')
      });
      const body = await merchantFetch(`/billing?${qs}`, { billing: true });
      if (body.code === 'BILLING_LOCKED') {
        setUnlocked(false);
        message.warning('账单会话已失效，请重新验证密码');
        return;
      }
      if (!body.success) {
        message.error(body.error || '加载账单失败');
        setBilling(null);
        return;
      }
      setBilling(body.data);
    } catch {
      message.error('网络异常');
      setBilling(null);
    } finally {
      setLoading(false);
    }
  }, [unlocked, activeTeamId, range]);

  const loadExportLogs = useCallback(async () => {
    if (!unlocked) return;
    const body = await merchantFetch('/billing/export-logs', { billing: true });
    if (body.success) setExportLogs(body.data || []);
  }, [unlocked]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    if (unlocked) {
      void loadTeams();
      void loadExportLogs();
    }
  }, [unlocked, loadTeams, loadExportLogs]);

  useEffect(() => {
    if (unlocked) void loadBilling();
  }, [unlocked, loadBilling]);

  const onSendSetupCode = async () => {
    setCodeSending(true);
    try {
      const body = await merchantFetch('/billing/setup/send-code', { method: 'POST', body: '{}' });
      if (!body.success) {
        message.error(body.error || '发送失败');
        return;
      }
      message.success(body.message || '验证码已发送');
    } finally {
      setCodeSending(false);
    }
  };

  const onSetup = async () => {
    const values = await setupForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await merchantFetch('/billing/setup', {
        method: 'POST',
        body: JSON.stringify({ code: values.code, password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '设置失败');
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      message.success('访问密码已设置');
      void loadStatus();
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onUnlock = async () => {
    const values = await unlockForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await merchantFetch('/billing/unlock', {
        method: 'POST',
        body: JSON.stringify({ password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '验证失败');
        void loadStatus();
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      message.success('已解锁账单管理');
    } finally {
      setAuthSubmitting(false);
    }
  };

  const onSendResetCode = async () => {
    setCodeSending(true);
    try {
      const body = await merchantFetch('/billing/reset/send-code', { method: 'POST', body: '{}' });
      if (!body.success) {
        message.error(body.error || '发送失败');
        return;
      }
      message.success(body.message || '验证码已发送');
    } finally {
      setCodeSending(false);
    }
  };

  const onReset = async () => {
    const values = await resetForm.validateFields();
    setAuthSubmitting(true);
    try {
      const body = await merchantFetch('/billing/reset', {
        method: 'POST',
        body: JSON.stringify({ code: values.code, password: values.password })
      });
      if (!body.success) {
        message.error(body.error || '重置失败');
        return;
      }
      sessionStorage.setItem(BILLING_TOKEN_KEY, body.data.billingToken);
      setUnlocked(true);
      setResetOpen(false);
      resetForm.resetFields();
      message.success('访问密码已重置');
      void loadStatus();
    } finally {
      setAuthSubmitting(false);
    }
  };

  const doExport = async () => {
    if (!activeTeamId) {
      message.warning('请选择团队');
      return;
    }
    setExporting(true);
    try {
      const body = await merchantFetch('/billing/export', {
        method: 'POST',
        billing: true,
        body: JSON.stringify({
          teamId: activeTeamId,
          startDate: range[0].format('YYYY-MM-DD'),
          endDate: range[1].format('YYYY-MM-DD')
        })
      });
      if (body.code === 'BILLING_LOCKED') {
        setUnlocked(false);
        message.warning('账单会话已失效，请重新验证密码');
        return;
      }
      if (!body.success) {
        message.error(body.error || '导出失败');
        return;
      }
      if (!body.data?.orders?.length) {
        message.warning('当前没有可导出的账单数据');
        return;
      }
      writeExcel(body.data, range);
      message.success('已导出，并写入加密审计记录');
      void loadExportLogs();
    } finally {
      setExporting(false);
    }
  };

  const columns: ColumnsType<BillingOrder> = useMemo(
    () => [
      {
        title: '完成时间',
        dataIndex: 'completedAt',
        width: 160,
        render: (v) => fmtDate(v)
      },
      {
        title: '类型',
        dataIndex: 'category',
        width: 100,
        render: (v) => <Tag>{v}</Tag>
      },
      {
        title: '摘要',
        dataIndex: 'itemSummary',
        ellipsis: true,
        render: (v, row) => v || row.category
      },
      { title: '申请人', dataIndex: 'applicant', width: 100 },
      {
        title: '结算金额',
        dataIndex: 'settleAmount',
        width: 120,
        align: 'right',
        render: (v) => <Text strong>{fmtMoney(v)}</Text>
      },
      {
        title: '优惠后价格',
        dataIndex: 'discountedAmount',
        width: 130,
        align: 'right',
        render: (v) => (v == null ? <Text type="secondary">—</Text> : <Text strong>{fmtMoney(v)}</Text>)
      },
      {
        title: '售后处理',
        width: 180,
        render: (_, row) =>
          row.afterSales?.length ? (
            <Space direction="vertical" size={2}>
              {row.afterSales.map((request) => (
                <Tag key={request._id}>
                  {request.type === 'return' ? '退货' : '换货'} · {AFTER_SALES_STATUS_TEXT[request.status]}
                </Tag>
              ))}
            </Space>
          ) : <Text type="secondary">—</Text>
      },
      {
        title: '售后调整',
        dataIndex: 'afterSalesAdjustment',
        width: 110,
        align: 'right',
        render: (value) => Number(value || 0) === 0 ? '—' : fmtMoney(value)
      },
      {
        title: '调整后金额',
        dataIndex: 'billingAdjustedAmount',
        width: 125,
        align: 'right',
        render: (value) => <Text strong>{fmtMoney(value)}</Text>
      },
      {
        title: '备注价格',
        width: 110,
        align: 'right',
        render: (_, row) =>
          row.merchantOrder?.platformOrderNote?.submittedAt
            ? fmtMoney(row.merchantOrder.platformOrderNote.price)
            : <Text type="secondary">—</Text>
      },
      {
        title: '备注链接',
        width: 220,
        ellipsis: true,
        render: (_, row) =>
          row.merchantOrder?.platformOrderNote?.submittedAt &&
          row.merchantOrder.platformOrderNote.link ? (
            <Button
              type="link"
              size="small"
              onClick={() =>
                Modal.info({
                  title: '平台下单链接',
                  content: (
                    <div style={{ wordBreak: 'break-all' }}>
                      {row.merchantOrder?.platformOrderNote?.link}
                    </div>
                  )
                })
              }
            >
              查看链接
            </Button>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: '快递',
        width: 160,
        render: (_, row) => {
          const list = row.trackingNumbers || [];
          if (!list.length) return <Text type="secondary">—</Text>;
          return list.map((t) => t.number).join('、');
        }
      }
    ],
    []
  );

  const logColumns: ColumnsType<ExportLog> = [
    { title: '时间', dataIndex: 'createdAt', width: 160, render: (v) => fmtDate(v) },
    {
      title: '操作人',
      width: 110,
      render: (_, r) => r.exporterName || r.exporterUsername || '—'
    },
    { title: '团队', dataIndex: 'teamName', width: 120, render: (v) => v || '—' },
    {
      title: '区间',
      width: 190,
      render: (_, r) => `${r.startDate || '—'} ~ ${r.endDate || '—'}`
    },
    { title: '笔数', dataIndex: 'orderCount', width: 70 },
    { title: '合计', dataIndex: 'totalAmount', width: 100, render: (v) => fmtMoney(v) },
    { title: 'IP', dataIndex: 'ip', width: 130, render: (v) => v || '—' },
    { title: '访问地', dataIndex: 'location', width: 140, render: (v) => v || '—' },
    { title: '设备', dataIndex: 'device', width: 200, ellipsis: true, render: (v) => v || '—' }
  ];

  const expandedRowRender = (order: BillingOrder) => {
    const items = lineItemsOf(order);
    const itemCols: ColumnsType<LineItem> = [
      { title: '品名', dataIndex: 'name', render: (v) => v || '—' },
      {
        title: '规格/型号',
        render: (_, r) => r.model || r.specification || r.cas || '—'
      },
      { title: '数量', dataIndex: 'amount', width: 80, render: (v) => v || '—' },
      { title: '单位', dataIndex: 'unit', width: 70, render: (v) => v || '—' },
      { title: '单价', dataIndex: 'price', width: 90, render: (v) => v || '—' },
      { title: '小计', dataIndex: 'total', width: 90, render: (v) => v || '—' },
      {
        title: '优惠后价格',
        dataIndex: 'discountedTotal',
        width: 110,
        render: (v) => (v == null ? '—' : fmtMoney(v))
      },
      {
        title: '渠道/厂商',
        render: (_, r) => r.purchaseChannel || r.vendor || '—'
      }
    ];
    return (
      <div style={{ padding: '4px 8px 12px' }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text type="secondary">
            收件：{order.recipientName || '—'} {order.recipientPhone || ''} ·{' '}
            {order.shippingAddress || '未填写地址'}
          </Text>
          <Text type="secondary">
            原报价 {fmtMoney(order.merchantOrder?.originalAmount)} · 确认价{' '}
            {order.merchantOrder?.quotedAmount != null
              ? fmtMoney(order.merchantOrder.quotedAmount)
              : '—'}{' '}
            · 接单 {fmtDate(order.acceptedAt)} · 发货 {fmtDate(order.shippedAt)}
          </Text>
          {items.length ? (
            <Table
              size="small"
              rowKey={(_, i) => `${order._id}-${i}`}
              pagination={false}
              columns={itemCols}
              dataSource={items}
            />
          ) : (
            <Text type="secondary">无商品明细</Text>
          )}
        </Space>
      </div>
    );
  };

  if (bootLoading && !status) {
    return (
      <div className="page-container full-height members-lims">
        <Card loading style={{ marginTop: 24 }} />
      </div>
    );
  }

  if (!unlocked) {
    return (
      <div className="page-container full-height members-lims">
        <nav className="mm-crumb" aria-label="breadcrumb">
          <HomeOutlined />
          <span>首页</span>
          <span className="mm-crumb-sep">/</span>
          <span>账单管理</span>
        </nav>
        <header className="mm-page-head">
          <h1 className="mm-title">账单管理</h1>
          <p className="mm-subtitle mb-0">敏感财务数据，需访问密码与邮箱验证保护。</p>
        </header>
        <div className="content-area px-1 px-md-2" style={{ maxWidth: 520 }}>
          <Card>
            <Space align="start" style={{ marginBottom: 16 }}>
              <LockOutlined style={{ fontSize: 28, color: 'var(--lims-primary, #00355f)' }} />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {status?.passwordConfigured ? '验证访问密码' : '首次设置访问密码'}
                </Title>
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  验证邮箱：{status?.emailMasked || '—'}
                </Paragraph>
              </div>
            </Space>

            {!status?.hasEmail && (
              <Alert
                type="error"
                showIcon
                className="mb-3"
                message="当前商家账号未绑定邮箱，请先在商铺中心完善联系邮箱后再设置账单密码。"
              />
            )}
            {status?.locked && (
              <Alert type="warning" showIcon className="mb-3" message="尝试次数过多，已临时锁定。" />
            )}

            {!status?.passwordConfigured ? (
              <Form form={setupForm} layout="vertical" onFinish={() => void onSetup()}>
                <Form.Item>
                  <Button
                    icon={<SafetyCertificateOutlined />}
                    loading={codeSending}
                    disabled={!status?.hasEmail}
                    onClick={() => void onSendSetupCode()}
                  >
                    发送邮箱验证码
                  </Button>
                </Form.Item>
                <Form.Item name="code" label="邮箱验证码" rules={[{ required: true, message: '请输入验证码' }]}>
                  <Input placeholder="6 位验证码" maxLength={6} />
                </Form.Item>
                <Form.Item
                  name="password"
                  label="设置访问密码"
                  rules={[
                    { required: true, message: '请设置密码' },
                    { min: 8, message: '至少 8 位' }
                  ]}
                >
                  <Input.Password placeholder="至少 8 位" />
                </Form.Item>
                <Form.Item
                  name="confirm"
                  label="确认密码"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: '请再次输入' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) return Promise.resolve();
                        return Promise.reject(new Error('两次密码不一致'));
                      }
                    })
                  ]}
                >
                  <Input.Password />
                </Form.Item>
                <Button type="primary" htmlType="submit" loading={authSubmitting} block>
                  确认设置并进入
                </Button>
              </Form>
            ) : (
              <Form form={unlockForm} layout="vertical" onFinish={() => void onUnlock()}>
                <Form.Item
                  name="password"
                  label="访问密码"
                  rules={[{ required: true, message: '请输入访问密码' }]}
                >
                  <Input.Password autoFocus />
                </Form.Item>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button type="primary" htmlType="submit" loading={authSubmitting} block>
                    解锁账单
                  </Button>
                  <Button type="link" onClick={() => setResetOpen(true)}>
                    忘记密码？通过邮箱验证码重置
                  </Button>
                </Space>
              </Form>
            )}
          </Card>
        </div>

        <Modal
          title="重置商家账单访问密码"
          open={resetOpen}
          onCancel={() => setResetOpen(false)}
          onOk={() => void onReset()}
          confirmLoading={authSubmitting}
          okText="确认重置"
        >
          <Form form={resetForm} layout="vertical">
            <Form.Item>
              <Button loading={codeSending} onClick={() => void onSendResetCode()}>
                发送验证码至 {status?.emailMasked}
              </Button>
            </Form.Item>
            <Form.Item name="code" label="验证码" rules={[{ required: true, message: '必填' }]}>
              <Input maxLength={6} />
            </Form.Item>
            <Form.Item
              name="password"
              label="新访问密码"
              rules={[
                { required: true, message: '必填' },
                { min: 8, message: '至少 8 位' }
              ]}
            >
              <Input.Password />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    );
  }

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>账单管理</span>
      </nav>

      <header className="mm-page-head d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h1 className="mm-title">账单管理</h1>
          <p className="mm-subtitle mb-0">
            按已加入团队查看已完成订单；导出将加密存档并记录 IP / 访问地 / 设备。
          </p>
        </div>
        <Space wrap>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="选择团队"
            style={{ minWidth: 220 }}
            value={activeTeamId || undefined}
            onChange={setActiveTeamId}
            options={teams.map((t) => ({
              value: t.teamId,
              label: t.authorized ? t.teamName : `${t.teamName}（历史）`
            }))}
          />
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(vals) => {
              if (vals?.[0] && vals?.[1]) setRange([vals[0], vals[1]]);
            }}
          />
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            loading={exporting}
            onClick={() => void doExport()}
          >
            导出 Excel
          </Button>
          <Button onClick={() => void loadBilling()}>刷新</Button>
          <Button
            onClick={() => {
              sessionStorage.removeItem(BILLING_TOKEN_KEY);
              setUnlocked(false);
            }}
          >
            锁定
          </Button>
        </Space>
      </header>

      <div className="content-area px-1 px-md-2">
        {!teams.length ? (
          <div className="am-table-shell bg-white rounded-3 border p-4">
            <Empty description="暂无已加入团队或已完成订单" />
          </div>
        ) : (
          <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3 mb-3">
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic title="已完成订单" value={billing?.summary.orderCount || 0} />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="结算合计"
                    value={billing?.summary.totalAmount || 0}
                    precision={2}
                    prefix="¥"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="售后调整合计"
                    value={billing?.summary.afterSalesAdjustmentAmount || 0}
                    precision={2}
                    prefix="¥"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="售后调整后合计"
                    value={billing?.summary.billingAdjustedTotalAmount || 0}
                    precision={2}
                    prefix="¥"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="优惠后合计"
                    value={billing?.summary.discountedTotalAmount || 0}
                    precision={2}
                    prefix="¥"
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card size="small">
                  <Statistic
                    title="账单区间"
                    value={`${range[0].format('YYYY-MM-DD')} ~ ${range[1].format('YYYY-MM-DD')}`}
                    valueStyle={{ fontSize: 16 }}
                  />
                </Card>
              </Col>
            </Row>

            <Table
              rowKey="_id"
              loading={loading}
              columns={columns}
              dataSource={billing?.orders || []}
              expandable={{ expandedRowRender }}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              locale={{ emptyText: '该团队在所选日期内暂无已完成订单' }}
              scroll={{ x: 860 }}
            />
          </div>
        )}

        <div className="am-table-shell bg-white rounded-3 border p-2 p-md-3">
          <Divider orientation="left" style={{ marginTop: 8 }}>
            导出记录（快照已 AES 加密存档）
          </Divider>
          <Table
            rowKey="_id"
            size="small"
            columns={logColumns}
            dataSource={exportLogs}
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: '暂无导出记录' }}
            scroll={{ x: 1100 }}
          />
        </div>
      </div>
    </div>
  );
};

export default MerchantBilling;
